import type { ReactNode } from 'react';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

// The frame the file viewers sit in: a job photo, a job document, an invoice PDF.
//
// Looking at any of them is the same gesture from the user's side — pick a thing,
// look at it — so they get the same window: same size, same header, same place to
// find Download. Shared rather than copied, because "these all match" is a promise
// that only holds if there is one thing to change.
//
// Lives in components/shared rather than beside the job-file viewers it started
// with: components/billing opens an invoice PDF in it, and nothing under
// components/ may import from pages/.

interface FileViewerShellProps {
  open: boolean;
  onClose: () => void;
  /** The filename: the dialog's accessible name and what the header shows. */
  title: string;
  /** Header actions, sitting to the right of the name. */
  actions?: ReactNode;
  /** Classes for the body surface — a photo wants a dark one, a document a light one. */
  bodyClassName?: string;
  children: ReactNode;
}

export function FileViewerShell({
  open,
  onClose,
  title,
  actions,
  bodyClassName = '',
  children,
}: FileViewerShellProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      {/* Sized off the viewport in both directions, so at 390px it fills the phone
          without becoming something the user has to scroll around. `dvh` rather
          than `vh` because iOS Safari's toolbar makes `vh` overshoot. */}
      <DialogContent
        aria-describedby={undefined}
        className="flex h-[85dvh] w-[95vw] max-w-5xl flex-col gap-0 overflow-hidden p-0 sm:w-[90vw]"
      >
        <DialogHeader className="shrink-0 space-y-0 border-b px-4 py-3">
          {/* Wraps at 390px: the actions drop under the name rather than
              squeezing it to nothing. The right padding clears Radix's close button. */}
          <div className="flex flex-wrap items-center gap-2 pr-6">
            <DialogTitle
              title={title}
              className="min-w-0 flex-1 truncate text-left text-sm font-medium"
            >
              {title}
            </DialogTitle>
            {actions}
          </div>
        </DialogHeader>

        {/* `relative` so a viewer can anchor its own controls to the body rather
            than to the whole dialog, which would put them over the header. */}
        <div className={`relative min-h-0 flex-1 ${bodyClassName}`}>{children}</div>
      </DialogContent>
    </Dialog>
  );
}
